"use client";

import { useState, useEffect } from "react";
import Image from "next/image";

interface RegionalHeroProps {
  images: string[];
  eyebrow: string;
  title: string;
  subtitle: string;
}

// Hero editorial con carrusel de imágenes en crossfade y contenido bottom-left
export default function RegionalHero({
  images,
  eyebrow,
  title,
  subtitle,
}: RegionalHeroProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1,
      );
    }, 6000);

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section className="relative h-[70vh] min-h-120 flex items-end overflow-hidden">
      {images.map((image, index) => (
        <div
          key={image}
          className={`absolute inset-0 transition-opacity duration-2000 ease-in-out ${
            index === currentImageIndex ? "opacity-100" : "opacity-0"
          }`}
        >
          <Image
            src={image}
            alt={title}
            fill
            priority={index === 0}
            className="object-cover"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/40 to-black/10" />

      <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
            <span className="w-8 h-px bg-white" />
            {eyebrow}
          </span>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
            {title}
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed">
            {subtitle}
          </p>
        </div>
      </div>

      <div className="absolute bottom-6 right-6 md:right-[10%] flex gap-2 z-10">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentImageIndex(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentImageIndex
                ? "bg-white w-8"
                : "bg-white/50 hover:bg-white/70 w-2"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
